/**
 * WooCommerce Store API Service
 */
import type { StoreAPIResponse, StoreAPICartResponse } from "../types";
import apiFetch from "@wordpress/api-fetch";

export type AddCartItemBatchParams = {
	id: number;
	quantity: number;
	variation?: {
		attribute: string;
		value: string;
	}[];
};

interface BatchResponse {
	responses: {
		body: StoreAPIResponse;
	}[];
}

export class StoreAPI {
	private baseUrl: string;
	private nonce: string;

	constructor(baseUrl: string, nonce: string) {
		this.baseUrl = baseUrl;
		this.nonce = nonce;

		// Initialize API Fetch with nonce middleware
		apiFetch.use(apiFetch.createNonceMiddleware(this.nonce));
	}

	/**
	 * Add a single item to cart directly
	 *
	 * @param id Product ID
	 * @param quantity Quantity
	 * @returns Promise with cart data
	 */
	async addSingleItemToCart(
		id: number,
		quantity = 1,
		variation?: { attribute: string; value: string }[],
		extensions?: Record<string, unknown>,
	): Promise<StoreAPIResponse> {
		try {
			const data: Record<string, unknown> = { id, quantity };
			if (variation && variation.length > 0) {
				data.variation = variation;
			}
			if (extensions) {
				data.extensions = extensions;
			}
			const response = await apiFetch<StoreAPIResponse>({
				url: `${this.baseUrl}/cart/add-item`,
				method: "POST",
				cache: "no-store",
				data,
				headers: {
					Nonce: this.nonce,
				},
			});

			return response;
		} catch (error) {
			console.error("Store API error adding single item:", error);
			throw error;
		}
	}

	/**
	 * Add multiple items to cart in a batch
	 *
	 * @param items Array of items with id, quantity and optional variation
	 * @returns Promise with cart data
	 */
	async addCartItems(
		items: AddCartItemBatchParams[],
	): Promise<StoreAPICartResponse> {
		try {
			// If only one item, use the direct method
			if (items.length === 1) {
				const response = await this.addSingleItemToCart(
					items[0].id,
					items[0].quantity,
				);
				// Convert to the new response format
				return {
					items: response.items.map((item) => ({
						key: "",
						id: Number(item.id),
						type: "variation",
						quantity: item.quantity,
						name: item.name,
					})),
					items_count: response.items_count,
					totals: {
						total_items: "",
						total_price: "",
						currency_symbol: "",
					},
				};
			}

			// Create batch requests with full paths as required by the API
			const requests = items.map((item) => ({
				path: "/wc/store/v1/cart/add-item", // Full path as required by the API
				method: "POST",
				cache: "no-store",
				body: {
					id: item.id,
					quantity: item.quantity,
					variation: item.variation,
				},
				headers: {
					Nonce: this.nonce,
				},
			}));

			// Use the full cart endpoint to get the complete cart data
			await apiFetch<BatchResponse>({
				url: `${this.baseUrl}/batch`,
				method: "POST",
				data: { requests },
			});

			// After batch request, get the full cart data
			await this.getCart();

			// Get the complete cart from the store API
			const fullCartResponse = await apiFetch<StoreAPICartResponse>({
				url: `${this.baseUrl}/cart`,
				method: "GET",
				cache: "no-store",
			});

			return fullCartResponse;
		} catch (error) {
			console.error("Store API error:", error);
			throw error;
		}
	}

	/**
	 * Get cart contents
	 *
	 * @returns Promise with cart data
	 */
	async getCart(): Promise<StoreAPIResponse> {
		try {
			const response = await apiFetch<StoreAPIResponse>({
				url: `${this.baseUrl}/cart`,
				method: "GET",
				cache: "no-store",
			});

			return response;
		} catch (error) {
			console.error("Store API error getting cart:", error);
			throw error;
		}
	}

	/**
	 * Get variation IDs that are currently in the cart
	 *
	 * @returns Promise with array of variation IDs
	 */
	async getCartVariationIds(): Promise<number[]> {
		try {
			const cart = await this.getCart();

			// Extract variation IDs from cart items
			// The Store API response doesn't directly include variation_id in the type
			// but the actual response from WooCommerce includes it
			const variationIds = cart.items
				.filter((item) => item.id)
				.map((item) => {
					// Convert string ID to number and return it
					// This assumes the ID is the variation ID when a variation is in the cart
					return Number(item.id);
				})
				.filter((id) => !Number.isNaN(id) && id > 0);

			return variationIds;
		} catch (error) {
			console.error("Failed to get variation IDs from cart:", error);
			return [];
		}
	}

	/**
	 * Remove an item from the cart by its key
	 *
	 * @param key The cart item key to remove
	 * @returns Promise with cart data
	 */
	async removeCartItem(key: string): Promise<StoreAPIResponse> {
		try {
			const response = await apiFetch<StoreAPIResponse>({
				url: `${this.baseUrl}/cart/remove-item`,
				method: "POST",
				cache: "no-store",
				data: {
					key,
				},
				headers: {
					Nonce: this.nonce,
				},
			});

			return response;
		} catch (error) {
			console.error("Store API error removing item:", error);
			throw error;
		}
	}

	/**
	 * Find cart item key by variation ID
	 *
	 * @param variationId The variation ID to find in the cart
	 * @returns Promise with the cart item key or null if not found
	 */
	async findCartItemKeyByVariationId(
		variationId: number,
	): Promise<string | null> {
		try {
			const cart = await this.getCart();

			// Find the cart item with the matching variation ID
			const cartItem = cart.items.find(
				(item) => Number(item.id) === variationId,
			);

			return cartItem?.key || null;
		} catch (error) {
			console.error("Failed to find cart item key:", error);
			return null;
		}
	}
}
